(() => {
  if (window.__MWBannerCarouselModuleLoaded) {
    window.__MWBannerCarouselInit?.();
    return;
  }
  window.__MWBannerCarouselModuleLoaded = true;

  const SELECTOR = '[data-mw-carousel]';
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  const networkConstrained = () => {
    const type = String(connection?.effectiveType || '').toLowerCase();
    return Boolean(connection?.saveData || type === 'slow-2g' || type === '2g');
  };

  const runWhenIdle = (callback, timeout = 1200) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout });
    } else {
      window.setTimeout(callback, Math.min(timeout, 600));
    }
  };

  function initCarousel(root) {
    if (!root || root.dataset.mwCarouselReady === 'true') return;
    root.dataset.mwCarouselReady = 'true';

    const slides = Array.from(root.querySelectorAll('[data-mw-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-mw-dot]'));
    const previous = root.querySelector('[data-mw-prev]');
    const next = root.querySelector('[data-mw-next]');
    const pause = root.querySelector('[data-mw-pause]');
    const status = root.querySelector('[data-mw-status]');
    const delay = Math.max(4000, Number(root.dataset.autoplayDelay) || 7000);

    if (slides.length < 2) {
      previous?.setAttribute('hidden', '');
      next?.setAttribute('hidden', '');
      pause?.setAttribute('hidden', '');
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    root.__mwCarouselAbortController?.abort();
    root.__mwCarouselAbortController = controller;

    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer = 0;
    let userPaused = false;
    let hoverPaused = false;
    let focusPaused = false;
    let isVisible = true;
    let touchStartX = null;

    const loadSlide = (targetIndex) => {
      const slide = slides[targetIndex];
      if (!slide) return;

      // Hydrate <picture> sources first so the browser can select the
      // correct art-directed mobile/desktop candidate before img.currentSrc.
      slide.querySelectorAll('source[data-mw-srcset]').forEach((source) => {
        source.srcset = source.dataset.mwSrcset;
        source.removeAttribute('data-mw-srcset');
      });

      const img = slide.querySelector('img[data-mw-src]');
      if (!img) return;
      if (img.dataset.mwSrcset) {
        img.srcset = img.dataset.mwSrcset;
        img.removeAttribute('data-mw-srcset');
      }
      img.loading = 'eager';
      img.src = img.dataset.mwSrc;
      img.removeAttribute('data-mw-src');
    };

    const shouldAutoplay = () => (
      root.isConnected &&
      isVisible &&
      !REDUCED.matches &&
      !networkConstrained() &&
      !userPaused &&
      !hoverPaused &&
      !focusPaused &&
      !document.hidden
    );

    const stop = () => {
      if (timer) window.clearTimeout(timer);
      timer = 0;
    };

    const schedule = () => {
      stop();
      if (!shouldAutoplay()) return;
      timer = window.setTimeout(() => show(index + 1, false), delay);
    };

    const updatePauseButton = () => {
      if (!pause) return;
      if (REDUCED.matches || networkConstrained()) {
        pause.hidden = true;
        return;
      }

      pause.hidden = false;
      pause.setAttribute('aria-pressed', userPaused ? 'true' : 'false');

      const pauseLabel = pause.dataset.labelPause || 'Pause carousel';
      const resumeLabel = pause.dataset.labelResume || 'Resume carousel';
      const pauseAria = pause.dataset.ariaPause || 'Pause automatic banner rotation';
      const resumeAria = pause.dataset.ariaResume || 'Resume automatic banner rotation';

      pause.setAttribute('aria-label', userPaused ? resumeAria : pauseAria);
      pause.setAttribute('title', userPaused ? resumeAria : pauseAria);

      const label = pause.querySelector('[data-mw-pause-label]');
      if (label) label.textContent = userPaused ? resumeLabel : pauseLabel;
    };

    function show(targetIndex, announce = true) {
      const normalized = (targetIndex + slides.length) % slides.length;
      loadSlide(normalized);

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === normalized;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.tabIndex = active ? 0 : -1;
      });

      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === normalized;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      index = normalized;
      if (announce && status) {
        const template = root.dataset.statusTemplate || 'Banner [index] of [total]';
        status.textContent = template.replace('[index]', String(index + 1)).replace('[total]', String(slides.length));
      }
      schedule();
    }

    previous?.addEventListener('click', () => show(index - 1), { signal });
    next?.addEventListener('click', () => show(index + 1), { signal });
    dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => show(dotIndex), { signal }));

    pause?.addEventListener('click', () => {
      userPaused = !userPaused;
      updatePauseButton();
      schedule();
    }, { signal });

    root.addEventListener('mouseenter', () => { hoverPaused = true; stop(); }, { signal });
    root.addEventListener('mouseleave', () => { hoverPaused = false; schedule(); }, { signal });
    root.addEventListener('focusin', () => { focusPaused = true; stop(); }, { signal });
    root.addEventListener('focusout', (event) => {
      if (root.contains(event.relatedTarget)) return;
      focusPaused = false;
      schedule();
    }, { signal });

    root.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches?.[0]?.clientX ?? null;
    }, { passive: true, signal });

    root.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const endX = event.changedTouches?.[0]?.clientX ?? touchStartX;
      const delta = endX - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) >= 45) show(index + (delta < 0 ? 1 : -1));
    }, { passive: true, signal });

    document.addEventListener('visibilitychange', schedule, { signal });
    REDUCED.addEventListener?.('change', () => {
      updatePauseButton();
      schedule();
    }, { signal });
    connection?.addEventListener?.('change', () => {
      updatePauseButton();
      schedule();
    }, { signal });

    let observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        isVisible = Boolean(entries[0]?.isIntersecting && entries[0].intersectionRatio >= 0.15);
        if (isVisible) schedule(); else stop();
      }, { threshold: [0, 0.15, 0.5] });
      observer.observe(root);
    }

    const cleanup = () => {
      stop();
      observer?.disconnect();
      controller.abort();
      delete root.__mwCarouselAbortController;
      root.dataset.mwCarouselReady = 'false';
    };
    root.__mwCarouselCleanup = cleanup;

    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.tabIndex = active ? 0 : -1;
    });
    dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    loadSlide(index);
    updatePauseButton();
    schedule();

    // On capable desktop connections only, hydrate the next slide after the
    // critical page load. Mobile and Save-Data users pay no speculative cost.
    const opportunisticPreload = () => {
      if (networkConstrained() || MOBILE.matches || REDUCED.matches) return;
      runWhenIdle(() => loadSlide((index + 1) % slides.length));
    };
    if (document.readyState === 'complete') opportunisticPreload();
    else window.addEventListener('load', opportunisticPreload, { once: true, signal });
  }

  const initAll = (scope = document) => scope.querySelectorAll?.(SELECTOR).forEach(initCarousel);
  window.__MWBannerCarouselInit = () => initAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(SELECTOR).forEach((root) => root.__mwCarouselCleanup?.());
  });
})();
